import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utils";
import { runbookActions } from "../../actions/runbook.actions";
import { runbookService } from "../../services/runbook.service";
import { runbookExecutionHistoryService } from "../../services/runbookExecutionHistory.service";
import { runbookVersionService } from "../../services/runbookVersion.service";
import { clientVariableLineService } from "../../services/clientVariableLine.service";
import { runbookTagService } from "../../services/runbookTag.service";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import EnhancedGrid from "../common/EnhancedGrid";
import Title from "../common/Title";
import { CiBoxList, CiClock1 } from "react-icons/ci";
import { FaPlayCircle } from "react-icons/fa";
import CategoryHelper from "../helpers/categoryHelper";
import StatusHelper from "../helpers/statusHelper";
import ConfirmModal from "../common/ConfirmModal";
import PicklistValueSelect from "../variable/PicklistValueSelect";
import {
  Toast,
  ToastContainer,
  Spinner,
  Modal,
  Button,
  Form,
  Collapse,
  InputGroup,
} from "react-bootstrap";
import { MdExpandMore, MdChevronRight, MdDragIndicator } from "react-icons/md";
import { TiTick } from "react-icons/ti";
import { MdDelete, MdEdit, MdError, MdGppGood, MdGppBad, MdCheckCircle, MdCancel, MdContentCopy } from "react-icons/md";
import { IoIosWarning } from "react-icons/io";
import { FaStar, FaRegStar, FaBell, FaBook } from "react-icons/fa";
import RunbookGridFilter from "./RunbookGridFilter";
import moment from "moment";
import { GiAlarmClock } from "react-icons/gi";
import GridHelper from "../helpers/gridHelper";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../contexts/TourContext";
import { usePermissions } from "../../hooks/usePermissions";
import EditRunbookModal from "./EditRunbookModal";
import EmailNotificationModal from "./EmailNotificationModal";

// #1840/#1841 — persist the My Runbooks sort order across navigation/reload
const SORT_STORAGE_KEY = "grid_sort_runbook";

// #2656 — Group by Tag defaults to on; persisted per client so a user who turns it off stays off.
const GROUP_BY_TAG_STORAGE_KEY_PREFIX = "grid_group_by_tag_runbook_";

const getSavedGroupByTag = (clientId) => {
  try {
    const saved = localStorage.getItem(GROUP_BY_TAG_STORAGE_KEY_PREFIX + clientId);
    return saved === null ? true : saved === "true";
  } catch (e) {
    return true;
  }
};

// #2646 — persist which tag groups are collapsed, per client (an internal user can switch
// clients via Impersonate). #2651: a group's own "collapsed by default" setting (managed under
// Administration > Runbook Groups) decides its state until a user explicitly toggles it here --
// from then on their own choice always wins over the admin default. Stored as {tagId: boolean}
// (explicit overrides only, not every group's current state), so changing the admin default still
// affects anyone who's never touched that particular group.
const COLLAPSED_GROUPS_STORAGE_KEY_PREFIX = "grid_collapsed_groups_runbook_";

const UNGROUPED_KEY = "__ungrouped__";

const getSavedCollapsedOverrides = (clientId) => {
  try {
    const saved = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY_PREFIX + clientId);
    const parsed = saved ? JSON.parse(saved) : {};
    // Pre-#2651 storage format was an array of tagIds the user had collapsed (back when every
    // group defaulted to expanded) -- migrate it into the new {tagId: boolean} override shape
    // instead of discarding everyone's existing choices.
    if (Array.isArray(parsed)) {
      return Object.fromEntries(parsed.map((tagId) => [tagId, true]));
    }
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

// #2646: the inner markup shared by both header variants below.
function GroupHeaderContent({ dragHandleProps, isCollapsed, color, label, count, isOver }) {
  return (
    <>
      {dragHandleProps && (
        <span
          {...dragHandleProps}
          className="text-muted me-1"
          style={{ cursor: "grab" }}
          onClick={(e) => e.stopPropagation()}
        >
          <MdDragIndicator size={18} />
        </span>
      )}
      {isCollapsed ? <MdChevronRight style={{ cursor: "pointer" }} /> : <MdExpandMore style={{ cursor: "pointer" }} />}
      {color && (
        <span
          className="ms-1 rounded-circle"
          style={{ width: "10px", height: "10px", backgroundColor: color, display: "inline-block" }}
        />
      )}
      <strong className="ms-1" style={{ cursor: "pointer" }}>
        {label}
      </strong>
      <span className="text-muted ms-2" style={{ cursor: "pointer" }}>
        ({count})
      </span>
      {isOver && <span className="small text-primary ms-auto me-2">Drop to move here</span>}
    </>
  );
}

// #2646: a real tag's group header -- sortable (drag to reorder groups) and, since useSortable
// is built on useDroppable, also a drop target for a dragged runbook row (re-tag). Must only ever
// be used for a group.key that's actually present in the enclosing SortableContext's items.
function SortableGroupHeader({ id, onToggle, isCollapsed, color, label, count }) {
  const { attributes, listeners, setNodeRef, transform, transition, isOver } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`d-flex align-items-center p-2 ${isOver ? "bg-primary-subtle border border-primary" : "bg-light"}`}
      onClick={onToggle}
    >
      <GroupHeaderContent
        dragHandleProps={{ ...attributes, ...listeners }}
        isCollapsed={isCollapsed}
        color={color}
        label={label}
        count={count}
        isOver={isOver}
      />
    </div>
  );
}

// #2646: the fixed "Ungrouped" header -- never draggable/reorderable (always sorts last), but
// still a drop target so dragging a runbook here un-tags it.
function DroppableUngroupedHeader({ id, onToggle, isCollapsed, label, count }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`d-flex align-items-center p-2 ${isOver ? "bg-primary-subtle border border-primary" : "bg-light"}`}
      onClick={onToggle}
    >
      <GroupHeaderContent
        dragHandleProps={null}
        isCollapsed={isCollapsed}
        color={null}
        label={label}
        count={count}
        isOver={isOver}
      />
    </div>
  );
}

// #2646: a runbook row in the grouped view, draggable onto any group header to re-tag it.
// The drag listeners live on a dedicated handle cell, not the row itself -- putting them on the
// whole <tr> made every plain click register as a (zero-distance) drag, which suppresses the
// click the row's own onClick relies on for selection. stopPropagation on the handle's click
// keeps a stray click there from also toggling selection.
function DraggableRunbookRow({ runbook, isSelected, onClick, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `row-${runbook.runbookId}`,
    data: { type: "runbook", runbook },
  });

  const style = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={isSelected ? "table-primary" : ""}
      onClick={onClick}
    >
      <td style={{ cursor: "grab" }} onClick={(e) => e.stopPropagation()} {...attributes} {...listeners}>
        <MdDragIndicator className="text-muted" />
      </td>
      {children}
    </tr>
  );
}

const getSavedSort = () => {
  try {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    return null;
  }
};

function RunbookGrid(props) {
  const { hideTitle, onAddRunbook, onOpenExecutions, onAddScheduler, onOpenDesigner } = props;
  const { hasPermission } = usePermissions(props.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const runbookReducer = useSelector((state) => state.runbook);
  const { activeTour, endTour } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [confirmRunModalShow, setConfirmRunModalShow] = useState(false);
  const [executeRunbook, setExecuteRunbook] = useState({});

  // #2619: lines flagged runtime-overridable (#2618) under any variable this runbook uses --
  // offered as editable inputs in the run confirmation, injected into just this one execution
  // without changing the stored value. overrideValues is keyed by variableLineId.
  const [overridableLines, setOverridableLines] = useState([]);
  const [overrideValues, setOverrideValues] = useState({});
  // #2636: picklist options for any overridable line whose lineType is "Picklist", keyed by
  // variableLineId -- fetched lazily, same call the line's own editor (VariableLineForm) makes.
  const [overridePicklistOptions, setOverridePicklistOptions] = useState({});
  const [confirmRunToastShow, setConfirmRunToastShow] = useState(false);
  const [confirmValidateModalShow, setConfirmValidateModalShow] =
    useState(false);
  const [validateRunbook, setValidateRunbook] = useState({});
  const [validateResultModalShow, setValidateResultModalShow] = useState(false);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);
  const [deleteRunbook, setDeleteRunbook] = useState({});
  const [selectedRunbook, setSelectedRunbook] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailNotificationModal, setShowEmailNotificationModal] = useState(false);
  const [confirmCancelModalShow, setConfirmCancelModalShow] = useState(false);
  const [cancelRunbook, setCancelRunbook] = useState({});
  const [cancelToastShow, setCancelToastShow] = useState(false);
  const [confirmReactivateModalShow, setConfirmReactivateModalShow] = useState(false);
  const [reactivateRunbook, setReactivateRunbook] = useState({});
  const [reactivateToastShow, setReactivateToastShow] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // #2625/#2642/#2646: grouping is by the runbook's Tag -- fetches a much larger page so every
  // matching runbook is grouped together instead of split across pages, and disables the normal
  // pager while active. collapsedOverrides holds each group header's state only where a user has
  // explicitly toggled it away from that group's admin-configured default, persisted per client in
  // localStorage. tags is the client's RunbookTag list -- drives each group's color/sort
  // order/default-collapsed state and the "drag a group to reorder" action; a runbook's own
  // tagName/tagColor (from RunbookResponse) are used as a display fallback if a tag is ever
  // referenced by a runbook but missing from this list (e.g. a stale fetch mid-edit elsewhere).
  const [groupByTag, setGroupByTag] = useState(() => getSavedGroupByTag(props.user.clientId));
  const [collapsedOverrides, setCollapsedOverrides] = useState(() =>
    getSavedCollapsedOverrides(props.user.clientId)
  );
  const [tags, setTags] = useState([]);
  const [activeDragId, setActiveDragId] = useState(null);
  // Copy-IDs support utility (Super User / Product Team only) — feedback toast state
  const [copyToastShow, setCopyToastShow] = useState(false);
  const [copyOk, setCopyOk] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("favoriteRunbooks");
    if (saved) {
      try {
        setFavorites(JSON.parse(saved));
      } catch (e) {
        setFavorites([]);
      }
    }
  }, []);

  const toggleFavorite = (runbook) => {
    const isFavorite = favorites.some(f => f.runbookId === runbook.runbookId);
    let updated;
    if (isFavorite) {
      updated = favorites.filter(f => f.runbookId !== runbook.runbookId);
    } else {
      updated = [...favorites, {
        runbookId: runbook.runbookId,
        runbookName: runbook.runbookName,
        runbookDescription: runbook.description,
        clientId: runbook.clientId,
        clientGuid: runbook.clientGuid,
        runbookGuid: runbook.runbookGuid
      }];
    }
    setFavorites(updated);
    localStorage.setItem("favoriteRunbooks", JSON.stringify(updated));
  };

  const isFavorite = (runbookId) => {
    return favorites.some(f => f.runbookId === runbookId);
  };

  const filterInitialState = {
    category: "",
    runbookId: "",
    runbookName: "",
    lastExecution: "",
    nextExecution: "",
    status: "",
  };
  const [filter, setFilter] = useState(filterInitialState);


  const refreshGrid = () => {
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;
    // #2625: grouped view wants every matching runbook in one response so groups aren't
    // split across pages -- the normal pager is hidden while grouped (see render below).
    queryParams["max_results"] = groupByTag ? 1000 : 50;

    // #1840/#1841 — if no sort in the URL (e.g. fresh navigation), fall back to
    // the last sort the user chose, persisted in localStorage.
    if (!queryParams["order_by"]) {
      const savedSort = getSavedSort();
      if (savedSort?.order_by) {
        queryParams["order_by"] = savedSort.order_by;
        queryParams["order_direction"] = savedSort.order_direction;
      }
    }

    if (showInactive) {
      queryParams["includeInactive"] = true; // include soft-deleted runbooks
    }

    dispatch(runbookActions.get(queryParams));
  };

  // #1840/#1841 — persist the chosen sort, then apply it via the URL as usual.
  const handleSort = (orderBy, orderDirection, loc, nav) => {
    try {
      localStorage.setItem(
        SORT_STORAGE_KEY,
        JSON.stringify({ order_by: orderBy, order_direction: orderDirection })
      );
    } catch (e) {
      // localStorage full or unavailable — ignore, sort still applies via URL
    }
    GridHelper.handleSortClick(orderBy, orderDirection, loc, nav);
  };

  // #1840/#1841 — effective sort for the indicator: URL wins, else remembered.
  const sortParams = Utils.getQueryStringParams(location);
  const savedSort = getSavedSort();
  const effectiveOrderBy = sortParams.order_by || savedSort?.order_by;
  const effectiveOrderDirection =
    sortParams.order_direction || savedSort?.order_direction;

  useEffect(() => {
    refreshGrid();

    // Initialize filter from URL
    let queryParams = Utils.getQueryStringParams(location);
    setFilter({
      category: queryParams["category"],
      runbookId: queryParams["runbookId"],
      runbookName: queryParams["runbookName"],
      lastExecution: queryParams["lastExecution"],
      nextExecution: queryParams["nextExecution"],
      status: queryParams["status"],
    });

    // eslint-disable-next-line
  }, [location, showInactive, groupByTag]);

  // Latest-ref so the 30s poll always calls the current refreshGrid — not one frozen at
  // whatever filter/sort/showInactive was active when the interval was first set up.
  // Without this, a runbook's status (e.g. an in-progress execution completing) never
  // updates while the user just sits on the page — previously it only refreshed on a
  // location change or after an action this component itself dispatched (execute/cancel/
  // reactivate/edit), which masked the missing poll as long as enough navigation happened.
  const refreshGridRef = useRef(refreshGrid);
  refreshGridRef.current = refreshGrid;
  useEffect(() => {
    const interval = setInterval(() => refreshGridRef.current(), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleClearFilters = () => {
    GridHelper.handleSearch(filterInitialState, location, navigate);
  };

  const handleExecuteRunbook = () => {
    setConfirmRunModalShow(false);
    setConfirmRunToastShow(true);

    // Dispatch action for execute runbook
    let requestBody = {
      runbookId: executeRunbook.runbookId,
      clientId: executeRunbook.clientId,
      runbookGuid: executeRunbook.runbookGuid,
      clientGuid: executeRunbook.clientGuid,
    };

    // #2619: only present when this runbook has at least one runtime-overridable line --
    // the API independently re-validates each entry against that same flag before this ever
    // reaches an execution, so this is never trusted blindly.
    if (overridableLines.length > 0) {
      requestBody.variableOverrides = overridableLines.map((line) => ({
        variableId: line.variableId,
        key: line.variableKey,
        value: overrideValues[line.variableLineId] ?? line.variableValue,
      }));
    }

    dispatch(runbookActions.execute(requestBody));
  };

  const handleValidateRunbook = () => {
    setConfirmValidateModalShow(false);
    setValidateResultModalShow(true);

    // Dispatch action for validate runbook
    let requestBody = {
      runbookId: validateRunbook.runbookId,
      clientId: validateRunbook.clientId,
      runbookGuid: validateRunbook.runbookGuid,
      clientGuid: validateRunbook.clientGuid,
      UpdateRunbookHeader: true,
    };

    dispatch(runbookActions.validate(requestBody));
  };

  const handleDeleteRunbook = () => {
    setConfirmDeleteModalShow(false);
    let requestParameters = {
      runbookId: deleteRunbook.runbookId,
      clientid: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };

    dispatch(runbookActions.remove(requestParameters));
  };

  const handleCancelRunbook = async () => {
    setConfirmCancelModalShow(false);
    setCancelToastShow(true);

    let executionID = null;
    try {
      const historyResponse = await runbookExecutionHistoryService.get({
        runbookId: cancelRunbook.runbookId,
        clientId: cancelRunbook.clientId,
        clientGuid: cancelRunbook.clientGuid,
        status: "InProcess",
        max_results: 1,
      });
      executionID = historyResponse?.data?.data?.[0]?.executionId ?? null;
    } catch (e) {
      console.warn("[Cancel Runbook] Could not fetch execution ID:", e);
    }

    const requestBody = {
      executionID,
      cancelledbyUser: props.user.email,
      clientId: cancelRunbook.clientId,
      clientGuid: cancelRunbook.clientGuid,
      runbookId: cancelRunbook.runbookId,
      runbookGuid: cancelRunbook.runbookGuid,
    };

    dispatch(runbookActions.cancel(requestBody));
  };

  useEffect(() => {
    if (!validateResultModalShow) {
      const wasValid = validateRunbook.isValid;
      const isValid = !runbookReducer?.validateResponse?.hasErrors;
      if (wasValid !== isValid) refreshGrid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateResultModalShow]);

  useEffect(() => {
    if (runbookReducer.executeResponse) {
      refreshGrid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runbookReducer.executeResponse]);

  useEffect(() => {
    if (runbookReducer.cancelResponse) {
      refreshGrid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runbookReducer.cancelResponse]);

  const handleReactivateRunbook = () => {
    setConfirmReactivateModalShow(false);
    setReactivateToastShow(true);

    dispatch(
      runbookActions.reactivate({
        runbookId: reactivateRunbook.runbookId,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      })
    );
  };

  useEffect(() => {
    if (runbookReducer.reactivateResponse) {
      refreshGrid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runbookReducer.reactivateResponse]);

  // Update selectedRunbook when grid data changes (after edit or refresh)
  useEffect(() => {
    if (selectedRunbook && runbookReducer.paginatedResult?.data) {
      const updatedRunbook = runbookReducer.paginatedResult.data.find(
        (r) => r.runbookId === selectedRunbook.runbookId
      );
      if (updatedRunbook) {
        setSelectedRunbook({ ...updatedRunbook, _statusRaw: updatedRunbook.status, _activeRaw: updatedRunbook.active });
        setTimeout(() => {
          document.querySelector("tr.table-primary")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runbookReducer.paginatedResult?.data]);

  // #1977: pre-select a runbook when returning from the builder (Apply & Close / Discard set
  // sessionStorage 'selectRunbook'). Highlight + scroll to that row once the list has loaded.
  useEffect(() => {
    const raw = sessionStorage.getItem("selectRunbook");
    if (!raw || !runbookReducer.paginatedResult?.data) return;
    let sel;
    try { sel = JSON.parse(raw); } catch { sessionStorage.removeItem("selectRunbook"); return; }
    const match = data?.find(
      (r) => r.runbookId === sel.runbookId && (!sel.runbookGuid || r.runbookGuid === sel.runbookGuid)
    );
    if (match) {
      setSelectedRunbook(match);
      setTimeout(() => {
        document.querySelector("tr.table-primary")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
    }
    sessionStorage.removeItem("selectRunbook");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runbookReducer.paginatedResult?.data]);

  const runbookFormattedName = (runbook) => {
    return `${runbook.runbookId} - ${runbook.runbookName} (${runbook.description})`;
  };

  useEffect(() => {
    if (activeTour === "runbooks") {
      setRunTour(true);
    }
  }, [activeTour]);

  // Force Joyride to re-render when selectedRunbook changes
  useEffect(() => {
    if (runTour && selectedRunbook) {
      // Trigger re-render by toggling runTour
      setRunTour(false);
      setTimeout(() => setRunTour(true), 0);
    }
  }, [selectedRunbook]);

  const tourSteps = [
    {
      target: "body",
      content: "Welcome to My Runbooks! Let me show you how to work with runbooks.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="filter-section"]',
      content: "Use these filters to search for specific runbooks by category, ID, name, or execution status.",
    },
    {
      target: '[data-tour="runbook-grid"]',
      content: "This grid shows all your available runbooks. Click on any row to select it and enable the action buttons. Try selecting one now!",
      spotlightClicks: true,
      disableBeacon: true,
      hideFooter: !selectedRunbook,
      disableOverlayClose: true,
    },
    {
      target: '[data-tour="action-buttons"]',
      content: "Great! Now that you've selected a runbook, these action buttons are enabled. Let's explore each one.",
    },
    {
      target: '[data-tour="favorite-button"]',
      content: "Click here to add a runbook to your favorites. Favorited runbooks appear on your dashboard for quick access.",
    },
    {
      target: '[data-tour="executions-button"]',
      content: "View the execution history for the selected runbook to see past runs and their results.",
    },
    ...(props.user.isSuperUser ? [{
      target: '[data-tour="scheduler-button"]',
      content: "Super users can schedule automatic runbook executions at specific times or intervals.",
    }] : []),
    {
      target: '[data-tour="run-button"]',
      content: "Execute the selected runbook immediately. You'll be asked to confirm before it runs.",
    },
    {
      target: '[data-tour="validate-button"]',
      content: "Validate the runbook configuration to ensure all variables, connectors, and settings are properly configured before running.",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      endTour("runbooks");
    }
  };

  let displayColumns = [
    "state",
    "category",
    "runbookName",
    "description",
    "lastExecution",
    "nextExecution",
    "status",
    "isValid",
    "netChangeField",
    "netChangeResult",
  ];

  const data = runbookReducer.paginatedResult?.data.map((r) => {
    return {
      ...r,
      lastExecution: r.lastExecution
        ? moment(Utils.convertToDateTime(r.lastExecution)).fromNow()
        : "-",
      nextExecution: r.nextExecution
        ? moment(Utils.convertToDateTime(r.nextExecution)).fromNow()
        : "-",
      category: CategoryHelper.getBadge(r.category, r.runbookId),
      _activeRaw: r.active,
      state: r.active === false ? "Deleted" : "Active",
      _statusRaw: r.status,
      status: StatusHelper.getBadge(r.status),
      netChangeField: r.netChangeField || "-",
      netChangeResult: r.netChangeResult && !r.netChangeResult.startsWith("1900-01-01") ? r.netChangeResult.replace("T", " ") : "-",
      isValid: r.isValid ? (
        <div className="text-success">
          <MdGppGood /> Valid
        </div>
      ) : (
        <div className="text-danger">
          <MdGppBad /> Invalid
        </div>
      ),
    };
  });

  // #2642/#2646: loads the client's RunbookTag entities -- drives each group's name/color/sort
  // order and is the authoritative list the "drag a group to reorder" action persists against
  // (not just whichever tags happen to be used by a runbook in the current page).
  const loadTags = () => {
    if (props.user.clientId) {
      runbookTagService.get({ clientId: props.user.clientId }).then((response) => {
        setTags(response.data || []);
      });
    }
  };

  useEffect(() => {
    if (groupByTag) {
      loadTags();
    }
    // eslint-disable-next-line
  }, [groupByTag, props.user.clientId]);

  // #2646/#2651: only explicit per-user overrides are persisted -- a group's admin-configured
  // default (or a newly created tag, which has never been overridden) is read fresh from `tags`
  // every render instead.
  useEffect(() => {
    try {
      localStorage.setItem(
        COLLAPSED_GROUPS_STORAGE_KEY_PREFIX + props.user.clientId,
        JSON.stringify(collapsedOverrides)
      );
    } catch (e) {
      // localStorage full or unavailable — collapse state just won't persist this session
    }
  }, [collapsedOverrides, props.user.clientId]);

  // #2656: Group by Tag defaults to on, but once a user flips it, their choice persists.
  useEffect(() => {
    try {
      localStorage.setItem(GROUP_BY_TAG_STORAGE_KEY_PREFIX + props.user.clientId, String(groupByTag));
    } catch (e) {
      // localStorage full or unavailable — preference just won't persist this session
    }
  }, [groupByTag, props.user.clientId]);

  // #2625/#2642/#2646: groups the already-fetched page of runbooks by TagId -- the data fetch
  // itself already asked for a much larger page while grouped (see refreshGrid), so this is
  // grouping what's effectively "all" matching runbooks, not just one page of them. Real tags
  // sort by their SortOrder (drag-to-reorder persists this); "Ungrouped" (no tag set) always
  // sorts last and is never draggable.
  const groupedEntries = useMemo(() => {
    if (!groupByTag || !data) return null;

    const byTagId = {};
    data.forEach((r) => {
      const key = r.tagId || UNGROUPED_KEY;
      (byTagId[key] = byTagId[key] || []).push(r);
    });

    const tagById = Object.fromEntries(tags.map((t) => [t.tagId, t]));

    const realGroups = Object.keys(byTagId)
      .filter((key) => key !== UNGROUPED_KEY)
      .map((tagId) => {
        const tag = tagById[tagId];
        const fallbackRow = byTagId[tagId][0];
        return {
          key: tagId,
          label: tag?.name ?? fallbackRow?.tagName ?? "Unknown Tag",
          color: tag?.color ?? fallbackRow?.tagColor ?? null,
          sortOrder: tag?.sortOrder ?? Number.MAX_SAFE_INTEGER,
          defaultCollapsed: tag?.defaultCollapsed ?? false,
          rows: byTagId[tagId],
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const ungroupedRows = byTagId[UNGROUPED_KEY];
    const ungroupedGroup = ungroupedRows
      ? [{
          key: UNGROUPED_KEY,
          label: "Ungrouped",
          color: null,
          sortOrder: Infinity,
          defaultCollapsed: false,
          rows: ungroupedRows,
        }]
      : [];

    return [...realGroups, ...ungroupedGroup];
  }, [groupByTag, data, tags]);

  const toggleGroup = (tagKey, currentlyCollapsed) => {
    setCollapsedOverrides((prev) => ({ ...prev, [tagKey]: !currentlyCollapsed }));
  };

  // #2646: drag-and-drop sensors shared by both the group-reorder and runbook-retag contexts.
  // activationConstraint is a second line of defense on top of moving listeners off the row --
  // without it, even a click directly on the drag handle would register as a zero-distance drag.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleRetagRunbook = (runbook, newTagId) => {
    if ((runbook.tagId || null) === (newTagId || null)) return;

    runbookService
      .setTag({
        runbookId: runbook.runbookId,
        clientId: runbook.clientId,
        clientGuid: runbook.clientGuid,
        tagId: newTagId,
      })
      .then(refreshGrid);
  };

  const handleReorderGroups = (reorderedTagIds) => {
    runbookTagService
      .reorder({ clientId: props.user.clientId, tagIds: reorderedTagIds })
      .then(loadTags);
  };

  const handleGroupDragEnd = ({ active, over }) => {
    setActiveDragId(null);
    if (!over) return;

    if (active.data.current?.type === "runbook") {
      const newTagId = over.id === UNGROUPED_KEY ? null : over.id;
      handleRetagRunbook(active.data.current.runbook, newTagId);
      return;
    }

    if (active.id === over.id) return;

    const realGroupKeys = groupedEntries.filter((g) => g.key !== UNGROUPED_KEY).map((g) => g.key);
    const oldIndex = realGroupKeys.indexOf(active.id);
    const newIndex = realGroupKeys.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    handleReorderGroups(arrayMove(realGroupKeys, oldIndex, newIndex));
  };

  const handleGroupedRowClick = (runbook) => {
    if (
      selectedRunbook?.runbookId === runbook.runbookId &&
      selectedRunbook?.runbookGuid === runbook.runbookGuid
    ) {
      setSelectedRunbook(null);
    } else {
      setSelectedRunbook(runbook);
    }
  };

  // A deleted (inactive) runbook can only be reactivated — every other action
  // is disabled until it's active again.
  const isInactiveSelected = selectedRunbook?._activeRaw === false;

  // Copy the selected runbook's identifiers (client + runbook, id + guid) to the
  // clipboard as labeled lines. Gated to Super Users / Product Team at the button.
  const handleCopyIds = async () => {
    if (!selectedRunbook) return;
    const text = [
      `clientId: ${selectedRunbook.clientId ?? ""}`,
      `clientGuid: ${selectedRunbook.clientGuid ?? ""}`,
      `runbookId: ${selectedRunbook.runbookId ?? ""}`,
      `runbookGuid: ${selectedRunbook.runbookGuid ?? ""}`,
    ].join("\n");

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts where the async Clipboard API is unavailable
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopyOk(true);
    } catch (e) {
      setCopyOk(false);
    } finally {
      setCopyToastShow(true);
    }
  };

  const isPrivileged = props.user?.isSuperUser || props.user?.isProductTeam;

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: "#cf7f00",
            textColor: "#334155",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#cf7f00",
            borderRadius: "0.5rem",
            fontSize: "14px",
            padding: "10px 20px",
          },
          buttonBack: {
            color: "#cf7f00",
            marginRight: "10px",
          },
          buttonSkip: {
            color: "#64748b",
          },
          tooltip: {
            borderRadius: "0.75rem",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          },
          tooltipContent: {
            padding: "20px",
          },
        }}
      />
      {!hideTitle && <Title text="My Runbooks" icon={<CiBoxList className="mb-1" />} />}

      {/* #2657: sticky so the search/action toolbar stays visible while scrolling a long list --
          barely noticeable in the paginated flat view (short page), but grouped view fetches up to
          1000 rows unpaginated, so without this the whole toolbar scrolls out of view. Sticks to
          whichever ancestor actually scrolls -- the desktop window body in the workspace
          experience, or the page itself in classic view. Both of those scrolling containers have
          1rem of their own top padding (DesktopWindow's `padding: 1rem`, classic Layout's `py-3`),
          which sticky's `top: 0` sticks below rather than covers -- without canceling it out here,
          a sliver of whatever row is scrolled behind that padding strip stays visible above this
          bar. The negative margin pulls this element up to the true top of the scroll area; the
          matching padding restores the original visual spacing. display: flow-root keeps the
          action-buttons bar's own bottom margin contained inside this box instead of collapsing
          through it, which otherwise left the first group header flush against the toolbar with
          no spacing. */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "var(--bg-primary)",
          marginTop: "-1rem",
          paddingTop: "1rem",
          display: "flow-root",
        }}
      >
      <RunbookGridFilter filter={filter} onClearFilters={handleClearFilters} />

      {/* Action Buttons Bar */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded" data-tour="action-buttons">

        {/* A deleted runbook can only be reactivated — hide the normal actions and show only Reactivate */}
        {!isInactiveSelected && (
          <>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || (!hasPermission("runbooks.execute") && !props.user.isAdmin && !props.user.isSuperUser)}
          onClick={() => {
            if (selectedRunbook) {
              setExecuteRunbook(selectedRunbook);
              setOverridableLines([]);
              setOverrideValues({});
              setOverridePicklistOptions({});
              runbookService
                .getOverridableVariableLines({
                  runbookId: selectedRunbook.runbookId,
                  clientId: selectedRunbook.clientId,
                })
                .then(
                  (response) => {
                    const lines = response.data || [];
                    setOverridableLines(lines);
                    setOverrideValues(
                      Object.fromEntries(lines.map((l) => [l.variableLineId, l.variableValue || ""]))
                    );

                    // #2636: only Picklist lines need their options fetched -- Text/Date render
                    // with just what GetOverridableVariableLines already returned.
                    lines
                      .filter((l) => l.lineType === "Picklist")
                      .forEach((l) => {
                        clientVariableLineService
                          .getPicklistValues({ variableLineId: l.variableLineId })
                          .then(
                            (picklistResponse) =>
                              setOverridePicklistOptions((prev) => ({
                                ...prev,
                                [l.variableLineId]: picklistResponse.data || [],
                              })),
                            () =>
                              setOverridePicklistOptions((prev) => ({ ...prev, [l.variableLineId]: [] }))
                          );
                      });
                  },
                  () => {
                    setOverridableLines([]);
                    setOverrideValues({});
                  }
                );
              setConfirmRunModalShow(true);
            }
          }}
          data-tour="run-button"
          title={
            !hasPermission("runbooks.execute") && !props.user.isAdmin && !props.user.isSuperUser
              ? "You don't have permission to execute runbooks"
              : !selectedRunbook
                ? "Select a runbook first"
                : "Execute the selected runbook"
          }
        >
          <FaPlayCircle className="mb-1 me-2" />
          Run
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || selectedRunbook._statusRaw !== "InProcess"}
          onClick={() => {
            if (selectedRunbook) {
              setCancelRunbook(selectedRunbook);
              setConfirmCancelModalShow(true);
            }
          }}
          title={
            !selectedRunbook
              ? "Select a runbook first"
              : selectedRunbook._statusRaw !== "InProcess"
                ? "Only runbooks with status 'In Process' can be cancelled"
                : "Cancel the selected runbook"
          }
        >
          <MdCancel className="mb-1 me-2" />
          Cancel
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || !hasPermission("history.view")}
          onClick={() => {
            if (selectedRunbook) {
              if (onOpenExecutions) {
                onOpenExecutions({
                  runbookId: selectedRunbook.runbookId,
                  runbookGuid: selectedRunbook.runbookGuid,
                  runbookName: selectedRunbook.runbookName,
                });
              } else {
                navigate(`executions?runbookId=${selectedRunbook.runbookId}&runbookGuid=${selectedRunbook.runbookGuid}`);
              }
            }
          }}
          data-tour="executions-button"
          title={
            !hasPermission("history.view")
              ? "You don't have permission to view execution history"
              : !selectedRunbook
                ? "Select a runbook first"
                : "View execution history for the selected runbook"
          }
        >
          <CiClock1 className="mb-1 me-2" />
          Latest Executions
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || !hasPermission("runbook-versions.edit")}
          onClick={async () => {
            if (!selectedRunbook || !hasPermission("runbook-versions.edit")) return;
            try {
              // #1977: "Open Runbook" opens the LATEST version (highest VERSIONNUM). The API returns
              // versions ordered by VERSIONNUM desc, so element [0] is the latest. Reuse the builder's
              // existing sessionStorage contract (importWorkflow + editingVersion).
              const response = await runbookVersionService.get({
                runbookId: selectedRunbook.runbookId,
                runbookGuid: selectedRunbook.runbookGuid,
              });
              const versions = response?.data;
              if (!Array.isArray(versions) || versions.length === 0) {
                alert("This runbook has no versions to open.");
                return;
              }
              const latest = versions.reduce((a, b) => (b.versionNum > a.versionNum ? b : a));
              if (latest.runbookString) {
                sessionStorage.setItem("importWorkflow", latest.runbookString);
              } else {
                sessionStorage.removeItem("importWorkflow");
              }
              sessionStorage.setItem("editingVersion", JSON.stringify({
                versionId: latest.versionId,
                versionNum: latest.versionNum,
                description: latest.description,
                runbookId: selectedRunbook.runbookId,
                runbookGuid: selectedRunbook.runbookGuid,
                runbookName: selectedRunbook.runbookName || "",
                readOnly: false,
              }));
              if (onOpenDesigner) {
                onOpenDesigner({ runbookName: selectedRunbook.runbookName });
              } else {
                navigate(`/runbook/workflow`);
              }
            } catch (e) {
              alert("Failed to open the runbook. Please try again.");
            }
          }}
          data-tour="open-runbook-button"
          title={!hasPermission("runbook-versions.edit") ? "You don't have permission to edit runbooks" : !selectedRunbook ? "Select a runbook first" : "Open the latest version of this runbook in the builder"}
        >
          <FaBook className="mb-1 me-2" />
          {onOpenDesigner ? "Designer" : "Open Runbook"}
        </Button>

        {/* #1977: "View Versions" removed from My Runbooks — it now lives inside the workflow editor. */}

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || !hasPermission("runbooks.validate")}
          onClick={() => {
            if (selectedRunbook && hasPermission("runbooks.validate")) {
              setValidateRunbook(selectedRunbook);
              setConfirmValidateModalShow(true);
            }
          }}
          data-tour="validate-button"
          title={!hasPermission("runbooks.validate") ? "You don't have permission to validate runbooks" : !selectedRunbook ? "Select a runbook first" : "Validate the selected runbook"}
        >
          <MdGppGood className="mb-1 me-2" />
          Validate
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || !hasPermission("runbooks.edit")}
          onClick={() => {
            if (selectedRunbook && hasPermission("runbooks.edit")) {
              setShowEditModal(true);
            }
          }}
          title={!hasPermission("runbooks.edit") ? "You don't have permission to edit runbooks" : !selectedRunbook ? "Select a runbook first" : "Edit the selected runbook header"}
        >
          <MdEdit className="mb-1 me-2" />
          Edit Header
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook}
          onClick={() => {
            if (selectedRunbook) {
              setShowEmailNotificationModal(true);
            }
          }}
          title={!selectedRunbook ? "Select a runbook first" : "Manage email notifications for this runbook"}
        >
          <FaBell className="mb-1 me-2" />
          Email Notifications
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || !hasPermission("runbooks.favorite")}
          onClick={() => {
            if (selectedRunbook && hasPermission("runbooks.favorite")) {
              toggleFavorite(selectedRunbook);
            }
          }}
          data-tour="favorite-button"
          title={!hasPermission("runbooks.favorite") ? "You don't have permission to manage favorites" : !selectedRunbook ? "Select a runbook first" : selectedRunbook && isFavorite(selectedRunbook.runbookId) ? "Remove from favorites" : "Add to favorites"}
        >
          {selectedRunbook && isFavorite(selectedRunbook.runbookId) ? (
            <><FaStar className="mb-1 me-2" />Remove Favorite</>
          ) : (
            <><FaRegStar className="mb-1 me-2" />Add Favorite</>
          )}
        </Button>

        {props.user.isSuperUser && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedRunbook}
            onClick={() => {
              if (selectedRunbook) {
                if (onAddScheduler) {
                  onAddScheduler(selectedRunbook.runbookId);
                } else {
                  navigate(`/scheduler/add?runbookId=${selectedRunbook.runbookId}`);
                }
              }
            }}
            data-tour="scheduler-button"
            title={!selectedRunbook ? "Select a runbook first" : "Add a scheduler for this runbook"}
          >
            <GiAlarmClock className="mb-1 me-2" />
            Add Scheduler
          </Button>
        )}

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedRunbook || !hasPermission("runbooks.delete")}
          onClick={() => {
            if (selectedRunbook && hasPermission("runbooks.delete")) {
              setDeleteRunbook(selectedRunbook);
              setConfirmDeleteModalShow(true);
            }
          }}
          data-tour="delete-button"
          title={
            !hasPermission("runbooks.delete")
              ? "You don't have permission to delete runbooks"
              : !selectedRunbook
                ? "Select a runbook first"
                : "Delete the selected runbook"
          }
        >
          <MdDelete className="mb-1 me-2" />
          Delete
        </Button>
          </>
        )}

        {/* Reactivate is only relevant for a deleted runbook — hide it otherwise */}
        {isInactiveSelected && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!hasPermission("runbooks.delete")}
            onClick={() => {
              if (hasPermission("runbooks.delete")) {
                setReactivateRunbook(selectedRunbook);
                setConfirmReactivateModalShow(true);
              }
            }}
            title={
              !hasPermission("runbooks.delete")
                ? "You don't have permission to reactivate runbooks"
                : "Reactivate the selected runbook"
            }
          >
            <MdCheckCircle className="mb-1 me-2" />
            Reactivate
          </Button>
        )}

        {/* Support utility: copy the client + runbook identifiers. Super Users / Product
            Team only — hidden entirely for everyone else. */}
        {isPrivileged && (
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedRunbook}
            onClick={handleCopyIds}
            title={
              !selectedRunbook
                ? "Select a runbook first"
                : "Copy clientId, clientGuid, runbookId and runbookGuid to the clipboard"
            }
          >
            <MdContentCopy className="mb-1 me-2" />
            Copy IDs
          </Button>
        )}


        <div className="ms-auto d-flex align-items-center gap-2">
          <Form.Check
            type="switch"
            id="group-runbooks-by-tag"
            label="Group by Tag"
            checked={groupByTag}
            onChange={(e) => {
              setGroupByTag(e.target.checked);
              setSelectedRunbook(null);
            }}
            className="align-self-center me-2 text-nowrap"
          />
          <Form.Check
            type="switch"
            id="show-inactive-runbooks"
            label="Include deleted"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              setSelectedRunbook(null);
            }}
            className="align-self-center me-2 text-nowrap"
          />
          {selectedRunbook && (
            <small className="text-muted align-self-center">
              Selected: <strong>{selectedRunbook.runbookId} - {selectedRunbook.runbookName}</strong>
            </small>
          )}
          {hasPermission("runbooks.create") && props.user.clientId && (
            <Button
              variant="outline-primary"
              size="sm"
              className="d-none d-md-inline-block"
              onClick={() => (onAddRunbook ? onAddRunbook() : navigate("add"))}
            >
              <CiBoxList className="mb-1 me-2" />
              Add Runbook
            </Button>
          )}
        </div>
      </div>
      </div>


      <div data-tour="runbook-grid">
        {groupByTag ? (
          <div className="border rounded">
            {runbookReducer.loading ? (
              <div className="text-center p-4">
                <Spinner animation="border" />
              </div>
            ) : groupedEntries && groupedEntries.length > 0 ? (
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragStart={({ active }) => setActiveDragId(active.id)}
                onDragEnd={handleGroupDragEnd}
                onDragCancel={() => setActiveDragId(null)}
              >
                <SortableContext
                  items={groupedEntries.filter((g) => g.key !== UNGROUPED_KEY).map((g) => g.key)}
                  strategy={verticalListSortingStrategy}
                >
                  {groupedEntries.map((group) => {
                    const isCollapsed = collapsedOverrides[group.key] ?? group.defaultCollapsed;
                    return (
                      <div key={group.key} className="border-bottom">
                        {group.key === UNGROUPED_KEY ? (
                          <DroppableUngroupedHeader
                            id={group.key}
                            onToggle={() => toggleGroup(group.key, isCollapsed)}
                            isCollapsed={isCollapsed}
                            label={group.label}
                            count={group.rows.length}
                          />
                        ) : (
                          <SortableGroupHeader
                            id={group.key}
                            onToggle={() => toggleGroup(group.key, isCollapsed)}
                            isCollapsed={isCollapsed}
                            color={group.color}
                            label={group.label}
                            count={group.rows.length}
                          />
                        )}
                        <Collapse in={!isCollapsed}>
                          <div>
                            <div className="table-responsive">
                              {/* table-layout: fixed + explicit widths -- each group renders its own
                                  <table>, so without this every group sizes its columns independently
                                  off its own content and nothing lines up from one group to the next. */}
                              <table className="table table-sm table-hover mb-0" style={{ tableLayout: "fixed" }}>
                                <thead>
                                  <tr>
                                    <th style={{ width: "5%" }}></th>
                                    <th style={{ width: "10%" }}>State</th>
                                    <th style={{ width: "15%" }}>Category</th>
                                    <th style={{ width: "25%" }}>Name</th>
                                    <th style={{ width: "30%" }}>Description</th>
                                    <th style={{ width: "15%" }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.rows.map((r) => (
                                    <DraggableRunbookRow
                                      key={`${r.runbookId}-${r.runbookGuid}`}
                                      runbook={r}
                                      isSelected={
                                        selectedRunbook?.runbookId === r.runbookId &&
                                        selectedRunbook?.runbookGuid === r.runbookGuid
                                      }
                                      onClick={() => handleGroupedRowClick(r)}
                                    >
                                      <td>{r.state}</td>
                                      <td>{r.category}</td>
                                      <td>{r.runbookName}</td>
                                      <td>{r.description}</td>
                                      <td>{r.status}</td>
                                    </DraggableRunbookRow>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </SortableContext>
                {/* #2646 follow-up: dnd-kit's DragOverlay positions itself with `position: fixed`
                    and doesn't portal on its own. Rendered in place, it inherits whatever
                    containing block its DOM ancestors impose -- inside a desktop window
                    (DesktopWindow.jsx combines `position: fixed` with an inline `transform` on the
                    same element), that makes the overlay track the window's box instead of the
                    real viewport, so it drifts further from the cursor the further the window has
                    been dragged. Portaling to document.body escapes any such ancestor; DndContext
                    is still its logical parent (React context, not DOM position), so drag state
                    keeps working. */}
                {createPortal(
                  <DragOverlay>
                    {activeDragId && activeDragId.toString().startsWith("row-") ? (
                      <div className="bg-white border rounded shadow-sm p-2 small">
                        {groupedEntries
                          .flatMap((g) => g.rows)
                          .find((r) => `row-${r.runbookId}` === activeDragId)?.runbookName}
                      </div>
                    ) : activeDragId ? (
                      <div className="bg-white border rounded shadow-sm p-2 small fw-bold">
                        {groupedEntries.find((g) => g.key === activeDragId)?.label}
                      </div>
                    ) : null}
                  </DragOverlay>,
                  document.body
                )}
              </DndContext>
            ) : (
              <div className="text-center p-4 text-muted">No runbooks found</div>
            )}
          </div>
        ) : (
          <EnhancedGrid
            gridId="runbook"
            data={data}
            displayColumns={displayColumns}
            emptyState={{
              icon: <FaBook size={80} className="text-muted mb-3" />,
              message: "No runbooks found",
              subtitle: "Create your first runbook to automate your workflows.",
              action: hasPermission("runbooks.create") && props.user.clientId ? (
                <Button variant="primary" size="sm" onClick={() => (onAddRunbook ? onAddRunbook() : navigate("add"))}>
                  Create Runbook
                </Button>
              ) : undefined,
            }}
            idColumn={"runbookId"}
            idColumn2={"runbookGuid"}
            selectedRunbook={selectedRunbook}
            onRowClick={(runbook) => {
              // Toggle selection: if same runbook clicked, deselect it
              if (selectedRunbook?.runbookId === runbook.runbookId &&
                  selectedRunbook?.runbookGuid === runbook.runbookGuid) {
                setSelectedRunbook(null);
              } else {
                setSelectedRunbook(runbook);
              }
            }}
            pageNumber={(runbookReducer.paginatedResult?.pageNumber ?? 1) - 1}
            totalPages={runbookReducer.paginatedResult?.totalPages ?? 0}
            loading={runbookReducer.loading}
            onPageChange={GridHelper.handlePageClick}
            onSort={handleSort}
            initialOrderBy={effectiveOrderBy ?? -1}
            initialOrderDirection={
              effectiveOrderDirection === "Desc" ? -1 : 1
            }
            useMobileCards={true}
          />
        )}
      </div>

      <ConfirmModal
        show={confirmDeleteModalShow}
        title={`Delete Runbook - ${deleteRunbook.runbookId}`}
        question={`Are you sure you want to delete ${runbookFormattedName(
          deleteRunbook
        )}?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => setConfirmDeleteModalShow(false)}
        handleConfirm={handleDeleteRunbook}
      />

      <ConfirmModal
        show={confirmReactivateModalShow}
        title={`Reactivate Runbook - ${reactivateRunbook.runbookId}`}
        question={
          <>
            Are you sure you want to reactivate{" "}
            <strong>{runbookFormattedName(reactivateRunbook)}</strong>?
            <br />
            <br />
            <small className="text-muted">
              Note: schedules were removed when this runbook was deleted and are
              not restored. You'll need to re-create any schedules after
              reactivating.
            </small>
          </>
        }
        noButtonText="No"
        yesButtonText="Yes, reactivate"
        handleClose={() => setConfirmReactivateModalShow(false)}
        handleConfirm={handleReactivateRunbook}
      />

      <ConfirmModal
        show={confirmCancelModalShow}
        title={`Cancel Runbook - ${cancelRunbook.runbookId}`}
        question={`Are you sure you want to cancel ${runbookFormattedName(cancelRunbook)}? This will stop the current execution.`}
        noButtonText="No"
        yesButtonText="Yes, cancel it"
        handleClose={() => setConfirmCancelModalShow(false)}
        handleConfirm={handleCancelRunbook}
      />

      <ConfirmModal
        show={confirmRunModalShow || confirmValidateModalShow}
        title={`${
          confirmRunModalShow
            ? `Execute Runbook - ${executeRunbook.runbookId}`
            : `Validate Runbook - ${validateRunbook.runbookId}`
        }`}
        question={
          <>
            {`Are you sure you want to ${
              confirmRunModalShow ? "execute" : "validate"
            } ${runbookFormattedName(
              confirmRunModalShow ? executeRunbook : validateRunbook
            )}?`}
            {confirmRunModalShow && overridableLines.length > 0 && (
              <div className="mt-3">
                <div className="small text-muted mb-2">
                  Override a value just for this run -- the saved value is not changed.
                </div>
                {overridableLines.map((line) => {
                  const value = overrideValues[line.variableLineId] ?? "";
                  const setValue = (newValue) =>
                    setOverrideValues((prev) => ({ ...prev, [line.variableLineId]: newValue }));

                  return (
                    <Form.Group key={line.variableLineId} className="mb-2">
                      <Form.Label className="small mb-1">{line.variableKey}</Form.Label>
                      {/* #2636: same Text/Picklist/Date control as the line's own editor (#2610) */}
                      {line.lineType === "Picklist" ? (
                        <PicklistValueSelect
                          value={value}
                          onChange={setValue}
                          options={overridePicklistOptions[line.variableLineId] || []}
                          placeholder="Select a value"
                        />
                      ) : line.lineType === "Date" ? (
                        <InputGroup size="sm">
                          <Form.Control
                            type="date"
                            value={Utils.displayDateToIso(value)}
                            onChange={(e) => setValue(Utils.isoDateToDisplay(e.target.value))}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={!value}
                            onClick={() => setValue("")}
                            title="Clear date"
                          >
                            ×
                          </Button>
                        </InputGroup>
                      ) : (
                        <Form.Control
                          size="sm"
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                        />
                      )}
                    </Form.Group>
                  );
                })}
              </div>
            )}
          </>
        }
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => {
          setConfirmRunModalShow(false);
          setConfirmValidateModalShow(false);
          setOverridableLines([]);
          setOverrideValues({});
          setOverridePicklistOptions({});
        }}
        handleConfirm={() => {
          confirmRunModalShow
            ? handleExecuteRunbook()
            : handleValidateRunbook();
        }}
      />

      <ToastContainer
        className="p-3"
        position={"bottom-end"}
        style={{ zIndex: 1 }}
      >
        <Toast
          onClose={() => setConfirmRunToastShow(false)}
          show={confirmRunToastShow}
        >
          <Toast.Header>
            <strong className="me-auto">
              Execute Runbook {executeRunbook.runbookId}
            </strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body>
            {runbookReducer.loadingExecute && (
              <>
                Sending {runbookFormattedName(executeRunbook)} to the queue...
                <Spinner
                  className="float-end"
                  size="sm"
                  variant="primary"
                ></Spinner>
                <div className="clearfix"></div>
              </>
            )}
            {!runbookReducer.loadingExecute && (
              <>
                {runbookReducer.executeResponse && (
                  <>
                    {runbookReducer.executeResponse.isSuccessful ? (
                      <TiTick className="text-success me-2 mb-1" />
                    ) : (
                      <IoIosWarning className="text-warning me-2 mb-1" />
                    )}
                    {runbookReducer.executeResponse.message}
                  </>
                )}
                {runbookReducer.executeError && (
                  <>
                    <MdError className="text-danger me-2 mb-1" />
                    {runbookReducer.executeError}
                  </>
                )}
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <ToastContainer
        className="p-3"
        position={"bottom-end"}
        style={{ zIndex: 1 }}
      >
        <Toast
          onClose={() => setCancelToastShow(false)}
          show={cancelToastShow}
        >
          <Toast.Header>
            <strong className="me-auto">
              Cancel Runbook {cancelRunbook.runbookId}
            </strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body>
            {runbookReducer.loadingCancel && (
              <>
                Cancelling {runbookFormattedName(cancelRunbook)}...
                <Spinner className="float-end" size="sm" variant="primary" />
                <div className="clearfix"></div>
              </>
            )}
            {!runbookReducer.loadingCancel && (
              <>
                {runbookReducer.cancelResponse && (
                  <>
                    <MdCancel className="text-secondary me-2 mb-1" />
                    Runbook cancellation requested successfully.
                  </>
                )}
                {runbookReducer.cancelError && (
                  <>
                    <MdError className="text-danger me-2 mb-1" />
                    {runbookReducer.cancelError}
                  </>
                )}
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <ToastContainer
        className="p-3"
        position={"bottom-end"}
        style={{ zIndex: 1 }}
      >
        <Toast
          onClose={() => setReactivateToastShow(false)}
          show={reactivateToastShow}
        >
          <Toast.Header>
            <strong className="me-auto">
              Reactivate Runbook {reactivateRunbook.runbookId}
            </strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body>
            {runbookReducer.loadingReactivate && (
              <>
                Reactivating {runbookFormattedName(reactivateRunbook)}...
                <Spinner className="float-end" size="sm" variant="primary" />
                <div className="clearfix"></div>
              </>
            )}
            {!runbookReducer.loadingReactivate && (
              <>
                {runbookReducer.reactivateResponse && (
                  <>
                    <MdCheckCircle className="text-success me-2 mb-1" />
                    Runbook reactivated. Schedules were removed on delete and
                    must be re-created.
                  </>
                )}
                {runbookReducer.reactivateError && (
                  <>
                    <MdError className="text-danger me-2 mb-1" />
                    {runbookReducer.reactivateError}
                  </>
                )}
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <ToastContainer
        className="p-3"
        position={"bottom-end"}
        style={{ zIndex: 1 }}
      >
        <Toast
          onClose={() => setCopyToastShow(false)}
          show={copyToastShow}
          delay={2500}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Copy IDs</strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body>
            {copyOk ? (
              <>
                <MdCheckCircle className="text-success me-2 mb-1" />
                Copied client &amp; runbook IDs to the clipboard.
              </>
            ) : (
              <>
                <MdError className="text-danger me-2 mb-1" />
                Couldn't copy to the clipboard.
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {validateRunbook && (
        <Modal
          show={validateResultModalShow}
          onHide={() => setValidateResultModalShow(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Validate Runbook - {validateRunbook.runbookId}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {runbookReducer.loadingValidate && (
              <>
                Validating {runbookFormattedName(validateRunbook)}...
                <Spinner
                  className="float-end"
                  size="sm"
                  variant="primary"
                ></Spinner>
                <div className="clearfix"></div>
              </>
            )}

            {!runbookReducer.loadingValidate && (
              <>
                {runbookReducer.validateResponse && (
                  <>
                    {!runbookReducer.validateResponse.hasErrors ? (
                      <div className="text-success">
                        <MdGppGood className="me-2 mb-1" />
                        Validation successful!
                      </div>
                    ) : (
                      <div className="text-danger">
                        <MdGppBad className="me-2 mb-1" />
                        {runbookReducer.validateResponse.message ||
                          "Validation failed. Please check the details."}
                      </div>
                    )}
                  </>
                )}

                {runbookReducer.validateError && (
                  <div className="text-danger">
                    <MdGppBad className="me-2 mb-1" />
                    {runbookReducer.validateError}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setValidateResultModalShow(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Edit Runbook Modal */}
      <EditRunbookModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        runbook={selectedRunbook ? runbookReducer.paginatedResult?.data.find(
          (r) => r.runbookId === selectedRunbook.runbookId
        ) : null}
        user={props.user}
        onSuccess={() => {
          refreshGrid();
        }}
      />

      {/* Email Notification Modal */}
      <EmailNotificationModal
        show={showEmailNotificationModal}
        onHide={() => setShowEmailNotificationModal(false)}
        runbook={selectedRunbook ? runbookReducer.paginatedResult?.data.find(
          (r) => r.runbookId === selectedRunbook.runbookId
        ) : null}
        user={props.user}
      />
    </>
  );
}

export default RunbookGrid;
